export function extractUrlTenantSlug(pathname: string): string | null {
  const match = pathname.match(/^\/t\/([^/]+)/);
  return match?.[1] ?? null;
}

/** @deprecated Prefer resolveAuthenticatedHomePath(role) — kept for student-default callers. */
export function tenantDashboardPath(tenantSlug: string): string {
  return `/t/${tenantSlug}/dashboard`;
}

export type TenantSlugGuardInput = {
  pathname: string;
  sessionTenantSlug?: string | null;
  isAuthenticated: boolean;
  role?: string | null;
};

/**
 * SPA-2: one-time links for a school that work whoever is signed in (or nobody): the invite page
 * explains a mismatch (AUTH-10), reset / verify act on the token alone.
 */
export const CROSS_TENANT_LINK_PREFIXES = ["/invite/", "/reset-password", "/verify-email"];

/** The page of a school that a session of another school may open: its sign-in and the links. */
export function isOpenToOtherSchoolSession(pathname: string): boolean {
  const match = pathname.match(/^\/t\/[^/]+(\/.*)?$/);
  const rest = match?.[1] ?? "";
  if (rest === "/login" || rest === "/login/") return true;
  return CROSS_TENANT_LINK_PREFIXES.some((prefix) =>
    prefix.endsWith("/") ? rest.startsWith(prefix) : rest === prefix || rest === `${prefix}/`,
  );
}

/** MT-1 S24: where a session of another school is sent — this school's sign-in page. */
export function otherSchoolLoginPath(urlTenantSlug: string): string {
  return `/t/${urlTenantSlug}/login?reason=other_school`;
}

export type TenantSlugGuardResult =
  { action: "allow" } | { action: "redirect"; redirectPath: string };

export function resolveTenantSlugGuard(input: TenantSlugGuardInput): TenantSlugGuardResult {
  const urlTenantSlug = extractUrlTenantSlug(input.pathname);
  if (!urlTenantSlug) {
    return { action: "allow" };
  }

  if (!input.isAuthenticated || !input.sessionTenantSlug) {
    return { action: "allow" };
  }

  if (urlTenantSlug === input.sessionTenantSlug) {
    return { action: "allow" };
  }

  // MT-1 S24 (FR-38, decisions §3 superseded): sessions are per school. A session of school A on
  // a page of school B goes to B's sign-in page, which names A and signs out of A first.
  if (isOpenToOtherSchoolSession(input.pathname)) {
    return { action: "allow" };
  }
  return { action: "redirect", redirectPath: otherSchoolLoginPath(urlTenantSlug) };
}
